import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";

export interface Estimate {
  id: number;
  businessId: number;
  customerId: number;
  estimateNumber: string;
  date?: string;
  issueDate: string;
  expiryDate: string;
  expiry_date?: string;
  status: "draft" | "sent" | "accepted" | "rejected" | "expired" | "converted";
  total: number;
  subtotal: number;
  taxAmount: number;
  notes: string | null;
  terms: string | null;
  customerName?: string;
  items?: EstimateItem[];
}

export interface EstimateItem {
  id: number;
  estimateId: number;
  productId: number | null;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  taxRate: number;
  productName?: string;
}

export interface CreateEstimateData {
  customerId: number;
  issueDate: string;
  expiryDate: string;
  status: "draft" | "sent" | "accepted" | "rejected" | "expired" | "converted";
  notes?: string;
  terms?: string;
  estimateNumber?: string;
  subtotal?: string;
  taxAmount?: string;
  total?: string;
  items: Array<{
    productId?: number;
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
    amount?: number;
    taxAmount?: number;
  }>;
}

export function useEstimates() {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  
  // Fetch all estimates for the current business
  const {
    data: estimates = [],
    isLoading,
    error,
    refetch
  } = useQuery<Estimate[]>({
    queryKey: [`/api/businesses/${currentBusiness?.id}/estimates`],
    queryFn: async () => {
      if (!currentBusiness?.id) return [];
      try {
        const res = await apiRequest("GET", `/api/businesses/${currentBusiness.id}/estimates`);
        
        if (!res.ok) {
          throw new Error("Failed to fetch estimates");
        }
        
        // Handle empty responses
        const text = await res.text();
        if (!text || text.trim() === '') {
          return [];
        }
        
        // Check if response is HTML instead of JSON
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          console.warn("Received HTML instead of JSON from estimates endpoint");
          return [];
        }
        
        // Try to parse JSON
        try {
          return JSON.parse(text);
        } catch (parseError) {
          console.error("Error parsing estimates JSON:", parseError);
          return [];
        }
      } catch (error) {
        console.error("Failed to fetch estimates:", error);
        return [];
      }
    },
    enabled: !!currentBusiness?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2
  });

  // Fetch a single estimate by ID
  const useEstimate = (estimateId: number) => {
    return useQuery({
      queryKey: [`/api/businesses/${currentBusiness?.id}/estimates/${estimateId}`],
      queryFn: async () => {
        if (!currentBusiness?.id || !estimateId) return null;
        const res = await apiRequest("GET", `/api/businesses/${currentBusiness.id}/estimates/${estimateId}`);
        if (!res.ok) {
          throw new Error("Failed to fetch estimate");
        }
        return res.json();
      },
      enabled: !!currentBusiness?.id && !!estimateId,
    });
  };

  // Create a new estimate
  const createEstimateMutation = useMutation({
    mutationFn: async (data: CreateEstimateData) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      try {
        console.log("Creating estimate with data:", JSON.stringify(data));
        const res = await apiRequest("POST", `/api/businesses/${currentBusiness.id}/estimates`, data);
        
        if (!res.ok) {
          // Try to parse error response
          try {
            const text = await res.text();
            
            // Check if response is HTML instead of JSON (common error case)
            if (text.includes('<!DOCTYPE') || text.includes('<html')) {
              throw new Error("Server returned an HTML error page instead of JSON");
            }
            
            // Try to parse as JSON
            if (text && text.trim() !== '') {
              const errorData = JSON.parse(text);
              throw new Error(errorData.message || "Failed to create estimate");
            } else {
              throw new Error("Failed to create estimate");
            }
          } catch (parseError) {
            console.error("Error parsing error response:", parseError);
            throw new Error("Failed to create estimate");
          }
        }
        
        // Handle successful response
        const text = await res.text();
        if (!text || text.trim() === '') {
          return { success: true };
        }
        
        try {
          return JSON.parse(text);
        } catch (parseError) {
          console.error("Error parsing success response:", parseError);
          return { success: true }; // Return generic success object if parsing fails
        }
      } catch (error) {
        console.error("Create estimate error:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Estimate created successfully",
      });
      
      // First, force a manual refetch
      refetch();
      
      // Then invalidate queries to ensure data consistency
      queryClient.invalidateQueries({ 
        queryKey: [`/api/businesses/${currentBusiness?.id}/estimates`] 
      });
      
      // Don't reload the page, instead navigate back to the estimates list
      // and wait for the query invalidation to trigger a refetch
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing estimate
  const updateEstimateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateEstimateData> }) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("PATCH", `/api/businesses/${currentBusiness.id}/estimates/${id}`, data);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update estimate");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Success",
        description: "Estimate updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/estimates`] });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/estimates/${data.id}`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete an estimate
  const deleteEstimateMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("DELETE", `/api/businesses/${currentBusiness.id}/estimates/${id}`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete estimate");
      }
      return id;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Estimate deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/estimates`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Convert estimate to invoice
  const convertToInvoiceMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!currentBusiness?.id) {
        throw new Error("No business selected");
      }
      const res = await apiRequest("POST", `/api/businesses/${currentBusiness.id}/estimates/${id}/convert`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to convert estimate to invoice");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Estimate converted to invoice successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/estimates`] });
      queryClient.invalidateQueries({ queryKey: [`/api/businesses/${currentBusiness?.id}/invoices`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    estimates,
    isLoading,
    error,
    refetch,
    useEstimate,
    createEstimateMutation,
    updateEstimateMutation,
    deleteEstimateMutation,
    convertToInvoiceMutation,
  };
}
